import { createContext, FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { PreviewAddedItem, PreviewItem } from './types'
import { ApiCatalogSearchItem } from '../../../api/catalog/types'
import { ApiChatPreviewQuotedItem, ApiUnitOfMeasureType } from '../../../api/chats/types/ApiChatPreview'
import { ApiUnitsOfMeasure } from '../../../api/chats/types/ApiRecalculateRequest'
import { ChatContext } from '../../../chat/context/ChatContext'

interface PreviewEditsContextValue {
  isChatLocked: boolean
  dirty: boolean
  items: PreviewItem[]
  adding: boolean
  selectItem: (index: number, selection: ApiCatalogSearchItem) => void
  removeItem: (index: number) => void
  startAdding: () => void
  cancelAdding: () => void
  addItem: (selection: ApiCatalogSearchItem) => void
  startEditingQuantity: (index: number) => void
  stopEditingQuantity: (index: number) => void
  updateUnit: (index: number, unitIndex: number) => void
  updateQuantity: (index: number, quantity: number) => void
  updateDate: (index: number, date: string | undefined) => void
}

const DEFAULT_UNIT_TYPE: ApiUnitOfMeasureType = 'units'

export function getOriginalUnitsOfMeasure(quotedItem: ApiChatPreviewQuotedItem): ApiUnitsOfMeasure {
  const entries = Object.entries(quotedItem.requestedQuantity) as [ApiUnitOfMeasureType, number | null][]
  const activeEntry = entries.find(([, value]) => value !== null && value > 0) as [ApiUnitOfMeasureType, number] | undefined

  return activeEntry
    ? {
        type: activeEntry[0],
        value: activeEntry[1],
      }
    : {
        type: DEFAULT_UNIT_TYPE,
        value: quotedItem.units,
      }
}

function getEntryUnitsOfMeasure(entry: PreviewItem): ApiUnitsOfMeasure {
  if (entry.type === 'added' || entry.type === 'edited') return entry.unitsOfMeasure
  if (entry.changedUnitsOfMeasure) return entry.changedUnitsOfMeasure
  const quotedItem = entry.previewItem.quotedItem
  return quotedItem ? getOriginalUnitsOfMeasure(quotedItem) : { type: DEFAULT_UNIT_TYPE, value: 1 }
}

function resolveUnitsOfMeasure(previous: ApiUnitsOfMeasure, newUnitsOfMeasure: ApiUnitOfMeasureType[]): ApiUnitsOfMeasure {
  return newUnitsOfMeasure.includes(previous.type) ? previous : { type: DEFAULT_UNIT_TYPE, value: 1 }
}

export const PreviewEditsContext = createContext({} as PreviewEditsContextValue)

export const PreviewEditsContextProvider: FC<PropsWithChildren<{ isChatLocked: boolean }>> = ({
  isChatLocked: initialLocked,
  children,
}) => {
  const { preview } = useContext(ChatContext)

  const [isChatLocked, setIsChatLocked] = useState(initialLocked)
  const [items, setItems] = useState<PreviewItem[]>([])
  const [adding, setAdding] = useState(false)

  useEffect(() => {
    if (preview) {
      setItems(preview.data.map((previewItem) => ({ type: 'original' as const, previewItem })))
      setAdding(false)
    }
  }, [preview])

  const dirty = useMemo(() => {
    const dataLength = preview?.data.length ?? 0
    if (items.length !== dataLength) return true
    return items.some((entry) => {
      if (entry.type !== 'original') return true
      if (entry.changedDate !== undefined) return true
      const changed = entry.changedUnitsOfMeasure
      if (changed === undefined) return false
      const quotedItem = entry.previewItem.quotedItem
      if (!quotedItem) return false
      const original = getOriginalUnitsOfMeasure(quotedItem)
      return changed.type !== original.type || changed.value !== original.value
    })
  }, [items, preview])

  const selectItem = useCallback((index: number, catalogItem: ApiCatalogSearchItem) => {
    setItems((prev) =>
      prev.map((entry, i) => {
        if (i !== index) return entry
        const unitsOfMeasure = resolveUnitsOfMeasure(getEntryUnitsOfMeasure(entry), catalogItem.unitsOfMeasure)
        if (entry.type === 'added') {
          return {
            ...entry,
            catalogItem,
            unitsOfMeasure,
          }
        }
        return {
          type: 'edited',
          previewItem: entry.previewItem,
          catalogItem,
          unitsOfMeasure,
        }
      }),
    )
    setIsChatLocked(true)
  }, [])

  const removeItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index))
    setIsChatLocked(true)
  }, [])

  const startAdding = useCallback(() => setAdding(true), [])

  const cancelAdding = useCallback(() => setAdding(false), [])

  const addItem = useCallback((selection: ApiCatalogSearchItem) => {
    const item: PreviewAddedItem = {
      type: 'added',
      catalogItem: selection,
      unitsOfMeasure: { type: DEFAULT_UNIT_TYPE, value: 1 },
    }

    setItems((items) => [...items, item])
    setAdding(false)
    setIsChatLocked(true)
  }, [])

  const startEditingQuantity = useCallback((index: number) => {
    setItems((prev) =>
      prev.map((entry, i) => {
        if (i !== index || entry.type !== 'original') return entry
        const quotedItem = entry.previewItem.quotedItem
        if (!quotedItem || entry.changedUnitsOfMeasure) return entry
        return { ...entry, changedUnitsOfMeasure: getOriginalUnitsOfMeasure(quotedItem) }
      }),
    )
  }, [])

  const stopEditingQuantity = useCallback((index: number) => {
    setItems((prev) =>
      prev.map((entry, i) => {
        if (i !== index || entry.type !== 'original' || !entry.changedUnitsOfMeasure) return entry
        const quotedItem = entry.previewItem.quotedItem
        if (!quotedItem) return entry
        const original = getOriginalUnitsOfMeasure(quotedItem)
        const changed = entry.changedUnitsOfMeasure
        if (changed.type !== original.type || changed.value !== original.value) return entry
        const { changedUnitsOfMeasure, ...rest } = entry
        return rest
      }),
    )
  }, [])

  const updateUnit = useCallback((index: number, unitIndex: number) => {
    setItems((prev) =>
      prev.map((entry, i) => {
        if (i !== index) return entry
        if (entry.type === 'original') {
          const quotedItem = entry.previewItem.quotedItem
          if (!quotedItem) return entry
          const unitType = quotedItem.skuUnitsOfMeasure[unitIndex]
          const prev = entry.changedUnitsOfMeasure ?? getOriginalUnitsOfMeasure(quotedItem)
          return { ...entry, changedUnitsOfMeasure: { ...prev, type: unitType } }
        }
        return {
          ...entry,
          unitsOfMeasure: { ...entry.unitsOfMeasure, type: entry.catalogItem.unitsOfMeasure[unitIndex] },
        }
      }),
    )
    setIsChatLocked(true)
  }, [])

  const updateQuantity = useCallback((index: number, quantity: number) => {
    setItems((prev) =>
      prev.map((entry, i) => {
        if (i !== index) return entry
        const unitType = getEntryUnitsOfMeasure(entry).type
        const value = unitType === 'units' ? Math.max(1, Math.floor(quantity)) : Math.max(0.01, quantity)
        if (entry.type === 'original') {
          const quotedItem = entry.previewItem.quotedItem
          if (!quotedItem) return entry
          const prev = entry.changedUnitsOfMeasure ?? getOriginalUnitsOfMeasure(quotedItem)
          return { ...entry, changedUnitsOfMeasure: { ...prev, value } }
        }
        return { ...entry, unitsOfMeasure: { ...entry.unitsOfMeasure, value } }
      }),
    )
    setIsChatLocked(true)
  }, [])

  const updateDate = useCallback((index: number, date: string | undefined) => {
    setItems((prev) =>
      prev.map((entry, i) => {
        if (i !== index) return entry
        if (entry.type === 'original') {
          const originalDate = entry.previewItem.quotedItem?.date
          const changedDate = date === originalDate ? undefined : date
          return { ...entry, changedDate }
        }
        return { ...entry, date }
      }),
    )
    setIsChatLocked(true)
  }, [])

  const value = useMemo(
    () => ({
      isChatLocked,
      dirty,
      items,
      adding,
      selectItem,
      removeItem,
      startAdding,
      cancelAdding,
      addItem,
      startEditingQuantity,
      stopEditingQuantity,
      updateUnit,
      updateQuantity,
      updateDate,
    }),
    [
      isChatLocked,
      dirty,
      items,
      adding,
      selectItem,
      removeItem,
      startAdding,
      cancelAdding,
      addItem,
      startEditingQuantity,
      stopEditingQuantity,
      updateUnit,
      updateQuantity,
      updateDate,
    ],
  )

  return <PreviewEditsContext.Provider value={value}>{children}</PreviewEditsContext.Provider>
}
